import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import mapApi from '~/apis/map';
import { handleApiError } from '~/utils/helper';
import { ILocationGeofenceItem, IPersonGeofenceItem, ITrackingItem } from '~/utils/interface/map';
import { useAppSelector } from '~/redux/hooks';
import { toFiniteNumber } from '~/utils/number';
import { resolveMapTrackingUsername } from '~/utils/mapTrackingUsername';
import useGeofenceOverlays from './useGeofenceOverlays';
import { extractStatisticPayloadArray } from './useMapStatistic';
import { usePersonStatus } from './usePersonStatus';
import type { TrackingDevice } from '../mapTrackingTypes';

export const POLLING_INTERVAL_MS = 15_000;

interface TrackingFallbackLabels {
  unknownDevice: string;
  unknownUser: string;
  noAddress: string;
}

const mergeTracking = (prev: ITrackingItem[], updates: ITrackingItem[]): ITrackingItem[] => {
  const updatesById = updates.reduce<Record<number, ITrackingItem>>((acc, item) => {
    acc[item.person_id] = item;
    return acc;
  }, {});
  const existingIds = new Set(prev.map((item) => item.person_id));
  const merged = prev.map((item) => updatesById[item.person_id] ?? item);
  const brandNew = updates.filter((item) => !existingIds.has(item.person_id));
  return brandNew.length > 0 ? [...merged, ...brandNew] : merged;
};

const mergeLocations = (prev: ILocationGeofenceItem[], incoming: ILocationGeofenceItem[]): ILocationGeofenceItem[] => {
  const incomingById = incoming.reduce<Record<number, ILocationGeofenceItem>>((acc, loc) => {
    acc[loc.id] = loc;
    return acc;
  }, {});
  const existingIds = new Set(prev.map((l) => l.id));
  const merged = prev.map((l) => incomingById[l.id] ?? l);
  const brandNew = incoming.filter((l) => !existingIds.has(l.id));
  return brandNew.length > 0 ? [...merged, ...brandNew] : merged;
};

const removeVietnameseTones = (value: string): string => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
};

const formatDisplayTime = (value: string): string => {
  if (!value) return '--';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('vi-VN', { hour12: false });
};

const toNullableNumber = (value: unknown): number | null => {
  return toFiniteNumber(value);
};

const toNullableStatus = (value: unknown): number | null => {
  const parsed = toNullableNumber(value);
  if (parsed === null) return null;
  if (parsed < 0 || parsed > 5) return null;
  return parsed;
};

const toTrackingDevice = (item: ITrackingItem, fallbackLabels: TrackingFallbackLabels): TrackingDevice => {
  const fullName = typeof item.full_name === 'string' ? item.full_name : '';

  return {
    person_id: item.person_id,
    device_name: item.device_name || fallbackLabels.unknownDevice,
    full_name: fullName || fallbackLabels.unknownUser,
    normalized_full_name: removeVietnameseTones(fullName).toLowerCase(),
    status: toNullableStatus(item.status),
    device_imei: item.device_imei || '--',
    device_type: item.device_type || '--',
    battery_percent: toNullableNumber(item.battery_percent),
    latitude: toNullableNumber(item.latitude),
    cccd: item.cccd || '--',
    longitude: toNullableNumber(item.longitude),
    speed: toNullableNumber(item.speed),
    address: item.address || fallbackLabels.noAddress,
    status_duration: typeof item.status_duration === 'number' ? Math.round(item.status_duration) : 0,
    trktime: formatDisplayTime(item.trktime || '--'),
    updatetime: formatDisplayTime(item.updatetime || item.trktime || '--'),
    today_distance: item.today_distance || '--',
    active_status: toNullableNumber(item.active_status),
  };
};

export function useMapTrackingPage() {
  const { t } = useTranslation('admin/map');
  const authUsername = useAppSelector((state) => state.auth.username);
  const userProfile = useAppSelector((state) => state.auth.userProfile);
  const selectedUser = useAppSelector((state) => state.auth.selectedUser);
  const mapTrackingUsername = useMemo(
    () => resolveMapTrackingUsername(userProfile?.username, authUsername, selectedUser?.username),
    [userProfile?.username, authUsername, selectedUser?.username]
  );
  const mapLoadErrorMessage = t('errors.loadMapData');

  const fallbackLabels = useMemo<TrackingFallbackLabels>(
    () => ({
      unknownDevice: t('device.unknownDevice'),
      unknownUser: t('device.unknownUser'),
      noAddress: t('device.noAddress'),
    }),
    [t]
  );

  const [devices, setDevices] = useState<TrackingDevice[]>([]);
  const [trackingList, setTrackingList] = useState<ITrackingItem[]>([]);
  const [personList, setPersonList] = useState<IPersonGeofenceItem[]>([]);
  const [locationList, setLocationList] = useState<ILocationGeofenceItem[]>([]);
  const [assignedLocationsByPersonId, setAssignedLocationsByPersonId] = useState<
    Record<number, ILocationGeofenceItem[]>
  >({});
  const [isTrackingLoading, setIsTrackingLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hoveredUserId, setHoveredUserId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const personStatuses = usePersonStatus();

  const lastTrackTimeUtcRef = useRef<string | null>(null);
  const pollInFlightRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!errorMessage) return;
    const timer = window.setTimeout(() => setErrorMessage(null), 5_000);
    return () => window.clearTimeout(timer);
  }, [errorMessage]);

  const normalizedSearchQuery = useMemo(
    () => removeVietnameseTones(debouncedQuery).trim().toLowerCase(),
    [debouncedQuery]
  );

  const filteredDevices = useMemo(() => {
    if (!normalizedSearchQuery) return devices;
    return devices.filter(
      (device) =>
        device.normalized_full_name.includes(normalizedSearchQuery) ||
        (device.device_imei !== '--' && device.device_imei.toLowerCase().includes(normalizedSearchQuery))
    );
  }, [devices, normalizedSearchQuery]);

  const geofenceOverlays = useGeofenceOverlays({
    trackingList,
    personList,
    locationList,
    assignedLocationsByPersonId,
  });

  const logApiRequest = useCallback(
    (time: string | null) => {
      console.log('API Request:', { username: mapTrackingUsername, time });
    },
    [mapTrackingUsername]
  );

  const handleSelectUser = useCallback((id: number | null) => {
    if (id === null) {
      setSelectedUserId(null);
      return;
    }
    setSelectedUserId((prev) => (prev === id ? null : id));
  }, []);

  const requestIdRef = useRef(0);

  const fetchInitialMapData = useCallback(async () => {
    if (!mapTrackingUsername) return;

    const requestId = ++requestIdRef.current;

    setErrorMessage(null);
    setIsTrackingLoading(true);

    try {
      logApiRequest(lastTrackTimeUtcRef.current);
      const trackingRes = await mapApi.getMapTracking(mapTrackingUsername);

      if (requestId !== requestIdRef.current) return;

      const nextTrackingList = Array.isArray(trackingRes.data) ? trackingRes.data : [];
      const latestTs = nextTrackingList.reduce<string>((acc, item) => {
        const ts = item.timestamp_utc;
        if (ts) {
          const tsStr = String(ts);
          if (!acc || tsStr > acc) return tsStr;
        }
        return acc;
      }, '');

      lastTrackTimeUtcRef.current = latestTs || null;
      setTrackingList(nextTrackingList);
      setDevices(nextTrackingList.map((item) => toTrackingDevice(item, fallbackLabels)));
    } catch (error) {
      if (requestId === requestIdRef.current) {
        setErrorMessage(mapLoadErrorMessage);
      }
      handleApiError(error);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsTrackingLoading(false);
      }
    }

    Promise.allSettled([
      mapApi.getPersons({ username: mapTrackingUsername }),
      mapApi.getLocations({ username: mapTrackingUsername }),
    ])
      .then((results) => {
        if (requestId !== requestIdRef.current) return;

        const [personRes, locationRes] = results;
        const overlayErrors: string[] = [];

        if (personRes.status === 'fulfilled') {
          setPersonList(extractStatisticPayloadArray(personRes.value.data) as IPersonGeofenceItem[]);
        } else {
          console.error('getPersons error:', personRes.reason);
          overlayErrors.push('persons_error');
        }

        if (locationRes.status === 'fulfilled') {
          setLocationList(extractStatisticPayloadArray(locationRes.value.data) as ILocationGeofenceItem[]);
        } else {
          console.error('getLocations error:', locationRes.reason);
          overlayErrors.push('locations_error');
        }

        if (overlayErrors.length > 0) {
          console.warn('Overlay errors:', overlayErrors.join(','));
        }
      })
      .catch((err) => {
        console.error('Overlay unexpected error:', err);
        if (requestId === requestIdRef.current) {
        }
      });
  }, [fallbackLabels, mapLoadErrorMessage, mapTrackingUsername, logApiRequest]);

  const pollTrackingUpdate = useCallback(async () => {
    if (!mapTrackingUsername) return;
    if (pollInFlightRef.current) return;
    pollInFlightRef.current = true;

    logApiRequest(lastTrackTimeUtcRef.current);

    const mergeDevices = (prev: TrackingDevice[], updates: ITrackingItem[]): TrackingDevice[] => {
      const updatesById = updates.reduce<Record<number, TrackingDevice>>((acc, item) => {
        acc[item.person_id] = toTrackingDevice(item, fallbackLabels);
        return acc;
      }, {});
      const existingIds = new Set(prev.map((d) => d.person_id));
      const merged = prev.map((d) => updatesById[d.person_id] ?? d);
      const brandNew = updates
        .filter((item) => !existingIds.has(item.person_id))
        .map((item) => toTrackingDevice(item, fallbackLabels));
      return brandNew.length > 0 ? [...merged, ...brandNew] : merged;
    };

    try {
      const trackingRes = await mapApi.getMapTracking(mapTrackingUsername, lastTrackTimeUtcRef.current);
      const updates = Array.isArray(trackingRes.data) ? trackingRes.data : [];

      if (updates.length > 0) {
        const latestTs = updates.reduce<string>(
          (acc, item) =>
            typeof item.timestamp_utc === 'string' && item.timestamp_utc > acc ? item.timestamp_utc : acc,
          ''
        );

        if (latestTs) lastTrackTimeUtcRef.current = latestTs;

        setTrackingList((prev) => mergeTracking(prev, updates));
        setDevices((prev) => mergeDevices(prev, updates));
      }

      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(mapLoadErrorMessage);
      handleApiError(error);
    } finally {
      pollInFlightRef.current = false;
    }
  }, [fallbackLabels, mapLoadErrorMessage, mapTrackingUsername, logApiRequest]);

  useEffect(() => {
    if (!mapTrackingUsername) return;
    lastTrackTimeUtcRef.current = null;
    fetchInitialMapData();
    const intervalId = window.setInterval(pollTrackingUpdate, POLLING_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [fetchInitialMapData, mapTrackingUsername, pollTrackingUpdate]);

  useEffect(() => {
    if (!selectedUserId) return;

    const personId = Number(selectedUserId);

    const fetchLocationsBySelectedUser = async () => {
      try {
        const res = await mapApi.getLocationsByPerson(personId);
        const incoming = extractStatisticPayloadArray(res.data) as ILocationGeofenceItem[];
        setAssignedLocationsByPersonId((prev) => ({
          ...prev,
          [personId]: incoming,
        }));
        if (incoming.length > 0) {
          setLocationList((prev) => mergeLocations(prev, incoming));
        }
      } catch (error) {
        setErrorMessage(mapLoadErrorMessage);
        handleApiError(error);
      }
    };

    fetchLocationsBySelectedUser();
  }, [mapLoadErrorMessage, selectedUserId]);

  /** Chỉ che UI khi chưa có dữ liệu lần đầu; poll nền giữ list/map hiển thị. */
  const isLoading = !mapTrackingUsername || (isTrackingLoading && devices.length === 0);

  return {
    mapTrackingUsername,
    devices,
    filteredDevices,
    locationList,
    geofenceOverlays,
    isLoading,
    errorMessage,
    fetchInitialMapData,
    hoveredUserId,
    setHoveredUserId,
    selectedUserId,
    handleSelectUser,
    searchQuery,
    setSearchQuery,
    personStatuses,
  };
}
